import type {
  AttachmentPageEmbedding,
  ChatMessage,
  Contact,
  Group,
  OutboxItem,
} from "./types.js";
import { cosineSim } from "./embeddings.js";

// IndexedDB namespacing: cada email tem o próprio DB. Evita vazar histórico
// entre logins distintos no mesmo navegador.
function dbName(email: string): string {
  return "wuwei__" + email.replace(/[^a-z0-9@._-]/gi, "_");
}

function open(email: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName(email), 3);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("messages")) {
        const s = db.createObjectStore("messages", { keyPath: "id" });
        s.createIndex("by_contact_ts", ["contact", "ts"], { unique: false });
      }
      if (!db.objectStoreNames.contains("contacts")) {
        db.createObjectStore("contacts", { keyPath: "email" });
      }
      if (!db.objectStoreNames.contains("groups")) {
        db.createObjectStore("groups", { keyPath: "id" });
      }
      // Fila de saída: o que foi escrito sem rede e ainda não teve ack.
      //
      // Em disco e não em memória porque o caso que ela existe para resolver é
      // justamente fechar o app sem rede — uma fila em memória perderia
      // exatamente as mensagens que ninguém mais tem.
      if (!db.objectStoreNames.contains("outbox")) {
        const o = db.createObjectStore("outbox", { keyPath: "id" });
        o.createIndex("by_ts", "ts", { unique: false });
      }
    };
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
  });
}

function tx<T>(
  db: IDBDatabase,
  stores: string | string[],
  mode: IDBTransactionMode,
  run: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(stores, mode);
    let result: T;
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
    Promise.resolve(run(t)).then((r) => (result = r));
  });
}

function reqP<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export class Store {
  private constructor(
    private readonly email: string,
    private readonly db: IDBDatabase,
  ) {}

  static async open(email: string): Promise<Store> {
    const db = await open(email);
    return new Store(email, db);
  }

  /** Insere mensagem (idempotente: se id já existe, ignora). */
  async putMessage(m: ChatMessage): Promise<boolean> {
    return tx(this.db, ["messages", "contacts"], "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const existing = await reqP<ChatMessage | undefined>(ms.get(m.id));
      if (existing) {
        // Atualiza apenas status (de pending → sent → delivered) sem regredir.
        if (m.direction === "out" && m.status) {
          const order = { pending: 0, sent: 1, delivered: 2, read: 3 } as const;
          if (order[m.status] > order[existing.status ?? "pending"]) {
            existing.status = m.status;
            ms.put(existing);
          }
        }
        return false;
      }
      ms.put(m);
      const cs = t.objectStore("contacts");
      const c = (await reqP<Contact | undefined>(cs.get(m.contact))) ?? {
        email: m.contact,
        lastTs: 0,
        lastText: "",
        unread: 0,
      };
      if (m.ts >= c.lastTs) {
        c.lastTs = m.ts;
        c.lastText =
          m.text ||
          (m.audio ? "🎤 áudio" : m.attachment ? `📎 ${m.attachment.name}` : "");
      }
      if (m.direction === "in") c.unread = (c.unread ?? 0) + 1;
      cs.put(c);
      return true;
    });
  }

  /**
   * Troca o texto de uma mensagem que já existe, sem mexer no resto.
   *
   * Serve para a resposta do modelo, que nasce vazia e cresce. Antes ela só
   * era gravada quando a geração terminava, e o que aparecia na tela durante
   * a escrita não existia em disco: fechar o app no meio apagava uma resposta
   * que a pessoa tinha acabado de ler. Agora ela é gravada no primeiro pedaço
   * e atualizada daí em diante.
   *
   * Silencioso quando a mensagem não existe: o caso normal é ela existir, e
   * uma exceção aqui interromperia a geração por causa da gravação — a ordem
   * errada de prioridades.
   */
  async atualizarTexto(id: string, texto: string): Promise<void> {
    await tx(this.db, ["messages", "contacts"], "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m) return;
      m.text = texto;
      ms.put(m);
      // A prévia da lista acompanha, senão a conversa mostra o texto novo e a
      // lista continua com o pedaço antigo.
      const cs = t.objectStore("contacts");
      const c = await reqP<Contact | undefined>(cs.get(m.contact));
      if (c && m.ts >= c.lastTs) {
        c.lastTs = m.ts;
        c.lastText = texto;
        cs.put(c);
      }
    });
  }

  async updateTranscript(id: string, text: string, lang: string): Promise<void> {
    await tx(this.db, ["messages", "contacts"], "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m) return;
      m.text = text;
      if (m.audio) m.audio.lang = lang;
      m.transcribing = false;
      ms.put(m);
      // Atualiza preview do contato se essa for a última mensagem
      const cs = t.objectStore("contacts");
      const c = await reqP<Contact | undefined>(cs.get(m.contact));
      if (c && c.lastTs === m.ts && text) {
        c.lastText = text;
        cs.put(c);
      }
    });
  }

  /** Persiste o vetor de embedding do transcript de áudio. Idempotente. */
  async setAudioEmbedding(
    id: string,
    embedding: Float32Array,
    modelId: string,
  ): Promise<void> {
    await tx(this.db, "messages", "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m || !m.audio) return;
      m.audio.embedding = embedding;
      m.audio.embeddingModelId = modelId;
      ms.put(m);
    });
  }

  /** Persiste embeddings página-a-página de um anexo (PDF/DOCX/PPTX/...).
   *  Idempotente: substitui o array inteiro. */
  async setAttachmentPageEmbeddings(
    id: string,
    pages: AttachmentPageEmbedding[],
    modelId: string,
  ): Promise<void> {
    await tx(this.db, "messages", "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m || !m.attachment) return;
      m.attachment.pageEmbeddings = pages;
      m.attachment.embeddingModelId = modelId;
      ms.put(m);
    });
  }

  /** Persiste vetor de embedding em mensagens de texto. */
  async setMessageEmbedding(
    id: string,
    embedding: Float32Array,
    modelId: string,
  ): Promise<void> {
    await tx(this.db, "messages", "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m) return;
      m.embedding = embedding;
      m.embeddingModelId = modelId;
      ms.put(m);
    });
  }

  /** Áudios que já têm transcrição mas ainda não foram embedados (ou foram
   *  embedados com versão antiga). Usado pra backfill ao iniciar. */
  async listAudiosNeedingEmbedding(currentModelId: string): Promise<ChatMessage[]> {
    return tx(this.db, "messages", "readonly", async (t) => {
      const ms = t.objectStore("messages");
      const all = await reqP<ChatMessage[]>(ms.getAll());
      return all.filter(
        (m) =>
          !!m.audio &&
          !!m.text &&
          !m.transcribing &&
          (!m.audio.embedding || m.audio.embeddingModelId !== currentModelId),
      );
    });
  }

  async listPendingOutgoing(): Promise<ChatMessage[]> {
    return tx(this.db, "messages", "readonly", async (t) => {
      const ms = t.objectStore("messages");
      const all = await reqP<ChatMessage[]>(ms.getAll());
      return all.filter(
        (m) => m.direction === "out" && (m.status ?? "pending") === "pending",
      );
    });
  }

  async editMessageText(id: string, text: string, editedTs: number): Promise<void> {
    await tx(this.db, ["messages", "contacts"], "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m) return;
      m.text = text;
      m.editedTs = editedTs;
      ms.put(m);
      const cs = t.objectStore("contacts");
      const c = await reqP<Contact | undefined>(cs.get(m.contact));
      if (c && c.lastTs === m.ts && text) {
        c.lastText = text;
        cs.put(c);
      }
    });
  }

  async markDeleted(id: string): Promise<ChatMessage | undefined> {
    return tx(this.db, ["messages", "contacts"], "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m) return undefined;
      m.deleted = true;
      m.text = "";
      delete m.audio;
      delete m.attachment;
      delete m.transcribing;
      ms.put(m);
      const cs = t.objectStore("contacts");
      const c = await reqP<Contact | undefined>(cs.get(m.contact));
      if (c && c.lastTs === m.ts) {
        c.lastText = "(mensagem apagada)";
        cs.put(c);
      }
      return m;
    });
  }

  /** Aplica add/remove de reaction in place. Retorna a mensagem
   *  atualizada (para re-render) ou undefined se a msg não existe mais. */
  async applyReaction(
    id: string,
    actor: string,
    emoji: string,
    op: "add" | "remove",
  ): Promise<ChatMessage | undefined> {
    return tx(this.db, "messages", "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m) return undefined;
      const r = (m.reactions ??= {});
      const list = (r[emoji] ??= []);
      const idx = list.indexOf(actor);
      if (op === "add" && idx < 0) list.push(actor);
      else if (op === "remove" && idx >= 0) list.splice(idx, 1);
      if (list.length === 0) delete r[emoji];
      if (Object.keys(r).length === 0) delete m.reactions;
      ms.put(m);
      return m;
    });
  }

  async getMessageById(id: string): Promise<ChatMessage | undefined> {
    return tx(this.db, "messages", "readonly", async (t) => {
      const ms = t.objectStore("messages");
      return reqP<ChatMessage | undefined>(ms.get(id));
    });
  }

  async markReadSent(id: string): Promise<void> {
    await tx(this.db, "messages", "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m || m.readSent) return;
      m.readSent = true;
      ms.put(m);
    });
  }

  async updateMessageStatus(
    id: string,
    status: "pending" | "sent" | "delivered" | "read",
  ): Promise<void> {
    await tx(this.db, "messages", "readwrite", async (t) => {
      const ms = t.objectStore("messages");
      const m = await reqP<ChatMessage | undefined>(ms.get(id));
      if (!m) return;
      const order = { pending: 0, sent: 1, delivered: 2, read: 3 } as const;
      if (order[status] > order[m.status ?? "pending"]) {
        m.status = status;
        ms.put(m);
      }
    });
  }

  async listMessages(contact: string): Promise<ChatMessage[]> {
    return tx(this.db, "messages", "readonly", async (t) => {
      const idx = t.objectStore("messages").index("by_contact_ts");
      const range = IDBKeyRange.bound([contact, 0], [contact, Number.MAX_SAFE_INTEGER]);
      const out: ChatMessage[] = [];
      return new Promise<ChatMessage[]>((resolve, reject) => {
        const cur = idx.openCursor(range);
        cur.onerror = () => reject(cur.error);
        cur.onsuccess = () => {
          const c = cur.result;
          if (!c) {
            resolve(out);
            return;
          }
          out.push(c.value as ChatMessage);
          c.continue();
        };
      });
    });
  }

  /** Página descendente do histórico de um contato.
   *  - Sem `before`: retorna as `limit` mensagens mais recentes.
   *  - Com `before`: retorna até `limit` mensagens com ts < before.
   *  Resultado em ordem cronológica ascendente. */
  async listMessagesPage(
    contact: string,
    opts: { before?: number; limit: number },
  ): Promise<ChatMessage[]> {
    return tx(this.db, "messages", "readonly", async (t) => {
      const idx = t.objectStore("messages").index("by_contact_ts");
      const range =
        opts.before != null
          ? IDBKeyRange.bound([contact, 0], [contact, opts.before], false, true)
          : IDBKeyRange.bound([contact, 0], [contact, Number.MAX_SAFE_INTEGER]);
      const out: ChatMessage[] = [];
      return new Promise<ChatMessage[]>((resolve, reject) => {
        const cur = idx.openCursor(range, "prev");
        cur.onerror = () => reject(cur.error);
        cur.onsuccess = () => {
          const c = cur.result;
          if (!c || out.length >= opts.limit) {
            out.reverse();
            resolve(out);
            return;
          }
          out.push(c.value as ChatMessage);
          c.continue();
        };
      });
    });
  }

  /** Busca substring case-insensitive em ChatMessage.text. Scan completo
   *  do object store: histórico vive 100% no cliente, N total é pequeno
   *  (chat pessoal), e é chamado sob debounce no input. */
  async searchMessages(
    query: string,
    limit = 100,
  ): Promise<{ contact: string; message: ChatMessage }[]> {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return tx(this.db, "messages", "readonly", async (t) => {
      const ms = t.objectStore("messages");
      const out: { contact: string; message: ChatMessage }[] = [];
      return new Promise<{ contact: string; message: ChatMessage }[]>((resolve, reject) => {
        const cur = ms.openCursor();
        cur.onerror = () => reject(cur.error);
        cur.onsuccess = () => {
          const c = cur.result;
          if (!c) {
            out.sort((a, b) => b.message.ts - a.message.ts);
            resolve(out.slice(0, limit));
            return;
          }
          const m = c.value as ChatMessage;
          if (!m.deleted && m.text && m.text.toLowerCase().includes(q)) {
            out.push({ contact: m.contact, message: m });
          }
          c.continue();
        };
      });
    });
  }

  /**
   * Busca semântica unificada: cobre `audio.embedding` (transcript de áudios),
   * `embedding` (texto puro) e `attachment.pageEmbeddings[]` (páginas de
   * documentos). Linear (O(N)) — em escala vai precisar de HNSW (WUW-55).
   * Devolve top-K acima de `minScore`, com a origem pra UI diferenciar.
   */
  async semanticSearch(
    queryVec: Float32Array,
    limit = 12,
    minScore = 0.5,
  ): Promise<
    {
      contact: string;
      message: ChatMessage;
      score: number;
      kind: "text" | "audio" | "pdf-page";
      pageNumber?: number;
    }[]
  > {
    return tx(this.db, "messages", "readonly", async (t) => {
      const ms = t.objectStore("messages");
      const all = await reqP<ChatMessage[]>(ms.getAll());
      const scored: {
        contact: string;
        message: ChatMessage;
        score: number;
        kind: "text" | "audio" | "pdf-page";
        pageNumber?: number;
      }[] = [];
      for (const m of all) {
        if (m.deleted) continue;
        // Páginas de documento — uma entrada por página acima do threshold.
        const pages = m.attachment?.pageEmbeddings;
        if (pages && pages.length > 0) {
          for (const p of pages) {
            const score = cosineSim(queryVec, p.vector);
            if (score >= minScore) {
              scored.push({
                contact: m.contact,
                message: m,
                score,
                kind: "pdf-page",
                pageNumber: p.pageNumber,
              });
            }
          }
        }
        // Áudio (transcript) — prioriza este pra mensagens com áudio.
        const audioEmb = m.audio?.embedding;
        if (audioEmb) {
          const score = cosineSim(queryVec, audioEmb);
          if (score >= minScore) {
            scored.push({ contact: m.contact, message: m, score, kind: "audio" });
          }
          continue;
        }
        // Texto puro (sem áudio).
        const textEmb = m.embedding;
        if (textEmb) {
          const score = cosineSim(queryVec, textEmb);
          if (score >= minScore) {
            scored.push({ contact: m.contact, message: m, score, kind: "text" });
          }
        }
      }
      scored.sort((a, b) => b.score - a.score);
      return scored.slice(0, limit);
    });
  }

  async listContacts(): Promise<Contact[]> {
    return tx(this.db, "contacts", "readonly", async (t) => {
      const cs = t.objectStore("contacts");
      const all = await reqP<Contact[]>(cs.getAll());
      all.sort((a, b) => b.lastTs - a.lastTs);
      return all;
    });
  }

  /** Apaga a conversa inteira deste dispositivo: todas as mensagens do contato
   *  e o próprio contato da lista. Não toca no servidor — é exclusão local, do
   *  lado de quem apagou; o outro participante continua com o histórico dele.
   *  Devolve quantas mensagens saíram. */
  async deleteConversation(contact: string): Promise<number> {
    return tx(this.db, ["messages", "contacts"], "readwrite", async (t) => {
      const idx = t.objectStore("messages").index("by_contact_ts");
      const ids = await reqP<IDBValidKey[]>(
        idx.getAllKeys(IDBKeyRange.bound([contact, -Infinity], [contact, Infinity])),
      );
      const ms = t.objectStore("messages");
      for (const k of ids) ms.delete(k);
      t.objectStore("contacts").delete(contact);
      return ids.length;
    });
  }

  async ensureContact(email: string): Promise<Contact> {
    return tx(this.db, "contacts", "readwrite", async (t) => {
      const cs = t.objectStore("contacts");
      const existing = await reqP<Contact | undefined>(cs.get(email));
      if (existing) return existing;
      const c: Contact = { email, lastTs: 0, lastText: "", unread: 0 };
      cs.put(c);
      return c;
    });
  }

  /** Insere/atualiza summary de contato vindo do servidor (sync inicial em
   *  device novo). Só sobrescreve `lastTs`/`lastText` se a info do server
   *  for mais nova que a local — protege overwrite em devices que já têm
   *  histórico recente. */
  async upsertContactSummary(
    email: string,
    lastTs: number,
    lastText: string,
  ): Promise<void> {
    await tx(this.db, "contacts", "readwrite", async (t) => {
      const cs = t.objectStore("contacts");
      const existing = await reqP<Contact | undefined>(cs.get(email));
      if (!existing) {
        cs.put({ email, lastTs, lastText, unread: 0 });
        return;
      }
      if (lastTs > existing.lastTs) {
        existing.lastTs = lastTs;
        existing.lastText = lastText;
        cs.put(existing);
      }
    });
  }

  /** Conta mensagens criadas em [sinceTs, ∞) por categoria. Usado pelas
   *  stats do dashboard. */
  async countSince(sinceTs: number): Promise<{
    received: number;
    sent: number;
    audios: number;
    media: number;
  }> {
    return tx(this.db, "messages", "readonly", async (t) => {
      const ms = t.objectStore("messages");
      const all = await reqP<ChatMessage[]>(ms.getAll());
      let received = 0,
        sent = 0,
        audios = 0,
        media = 0;
      for (const m of all) {
        if (m.ts < sinceTs || m.deleted) continue;
        if (m.direction === "in") received++;
        else sent++;
        if (m.audio) audios++;
        if (m.attachment) media++;
      }
      return { received, sent, audios, media };
    });
  }

  async putGroup(g: Group): Promise<void> {
    await tx(this.db, "groups", "readwrite", async (t) => {
      t.objectStore("groups").put(g);
    });
  }

  async listGroups(): Promise<Group[]> {
    return tx(this.db, "groups", "readonly", async (t) => {
      return reqP<Group[]>(t.objectStore("groups").getAll());
    });
  }

  async getGroup(id: string): Promise<Group | undefined> {
    return tx(this.db, "groups", "readonly", async (t) => {
      return reqP<Group | undefined>(t.objectStore("groups").get(id));
    });
  }

  async clearUnread(email: string): Promise<void> {
    await tx(this.db, "contacts", "readwrite", async (t) => {
      const cs = t.objectStore("contacts");
      const c = await reqP<Contact | undefined>(cs.get(email));
      if (c && c.unread) {
        c.unread = 0;
        cs.put(c);
      }
    });
  }

  // ---------- Fila de saída (offline) ----------

  /** Enfileira um frame que não conseguiu sair. A chave é o id da mensagem, o
   *  mesmo que o servidor usa para reconhecer reenvio — então enfileirar duas
   *  vezes a mesma mensagem não a duplica nem aqui nem lá. */
  async enqueue(item: OutboxItem): Promise<void> {
    await tx(this.db, "outbox", "readwrite", async (t) => {
      t.objectStore("outbox").put(item);
    });
  }

  /** A fila em ordem de escrita. Ordem importa: mandar fora de ordem faria a
   *  conversa do outro lado contar a história ao contrário. */
  async listOutbox(): Promise<OutboxItem[]> {
    return tx(this.db, "outbox", "readonly", async (t) => {
      const itens = await reqP<OutboxItem[]>(t.objectStore("outbox").getAll());
      return itens.sort((a, b) => a.ts - b.ts);
    });
  }

  /** Tira UM item, pela chave.
   *
   * Nunca reescrever a fila inteira com a lista lida antes: entre a leitura e
   * a escrita, outra aba (ou outro clique) pode ter enfileirado algo, e a
   * reescrita apagaria justamente essa mensagem. Perder mensagem em silêncio é
   * o pior defeito possível numa fila que existe para não perder mensagem. */
  async dequeue(id: string): Promise<void> {
    await tx(this.db, "outbox", "readwrite", async (t) => {
      t.objectStore("outbox").delete(id);
    });
  }

  async countOutbox(): Promise<number> {
    return tx(this.db, "outbox", "readonly", async (t) =>
      reqP<number>(t.objectStore("outbox").count()),
    );
  }
}
